import type { SvelteHTMLElements, HTMLButtonAttributes } from 'svelte/elements';
import type { Scalars, shipments_routes_steps } from '$lib/tools/graphdb';
import type { IconProps as LucideIconProps } from '@lucide/svelte';
import type { Component, Snippet } from 'svelte';

import { extract_coordinates } from './extract_coordinates'
import { shipments_utils } from './shipments_utils.svelte';
import { Loc } from '$lib/tools/locale.svelte';
import { SvelteDate } from 'svelte/reactivity';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { actions } from './actions';
import { cap_utils } from './cap_utils';
import { toast } from './toast';


export type EitherOr<T, K> = T | K

export type ModalProps = {
   disable_close_onclick_outside?: boolean;
   title?: string;
   no_title?: boolean;
   onclose?: () => any;
   card_props?: Omit<CardProps, "children" | "footer">,
   header_color?: string;
   class?: ElementType<'dialog'>['class'];
   children: Snippet;
   card_classes?: ElementType<'div'>['class'];
   level?: number;
   forced_classes?: ElementType<'dialog'>['class'];
   open: boolean;
   footer_snippet?: Snippet
};

export type CardProps = {
   title?: string | Snippet | null;
   header_classes?: ElementType<'div'>['class'];
   description?: string;
   children: Snippet;
   no_title?: boolean;
   footer?: Snippet;
   footer_classes?: ElementType<'div'>['class'];
   content_classes?: ElementType<'div'>['class'];
   classes?: ElementType<'div'>['class'];
}


export type NestedAccordionProps = {
   title?: string;
   subtitle?: string | number;
   required?: boolean;
   is_success?: boolean;
   title_inline?: boolean;
   nested_level?: number;
   disabled?: boolean;
   no_separator?: boolean;
   title_classes?: ElementClass<'div'>;
   subtitle_classes?: ElementClass<'div'>;
   open?: boolean;
   header_block?: Snippet;
   onclose?: () => any;
   onopen?: () => any;
   onchange?: (open: boolean) => any;
   id?: string;
   no_ribbon?: boolean;
   ribbon_classes?: ElementClass<'div'>;
   arrow_classes?: ElementClass<'div'>;
   outline?: boolean;
   secondary?: boolean;
   header_class?: ElementClass<'div'>;
   class?: ElementClass<'div'>;
   classes?: ElementClass<'div'>;
   content_classes?: ElementClass<'div'>;
   group_id?: string;
   icon?: IconProps;
   scroll_on_open?: boolean;
   image_id?: string;
   image_src?: string;
} & (
      | { content?: undefined; children: Snippet }
      | { content: Snippet<[() => void]>; children?: undefined }
   )

export type AccordionProps = {
   title?: string;
   subtitle?: string | number;
   required?: boolean;
   title_inline?: boolean;
   is_success?: boolean;
   no_separator?: boolean;
   title_classes?: ElementClass<'div'>;
   subtitle_classes?: ElementClass<'div'>;
   open?: boolean;
   disabled?: boolean;
   header_block?: Snippet;
   onclose?: () => any;
   id?: string;
   no_ribbon?: boolean;
   ribbon_classes?: ElementClass<'div'>;
   outline?: boolean;
   secondary?: boolean;
   header_class?: ElementClass<'div'>;
   class?: ElementClass<'div'>;
   classes?: ElementClass<'div'>;
   content_classes?: ElementClass<'div'>;
   group_id?: string;
   icon?: IconProps;
   scroll_on_open?: boolean;
   image_id?: string;
   image_src?: string;
} & (
      | { content?: undefined; children: Snippet }
      | { content: Snippet<[() => void]>; children?: undefined }
   )



export type IconProps =
   | {
      source: 'lucide';
      rounded_classes?: boolean;
      name: string;
      no_colors?: boolean;
      classes?: ElementClass<'div'>;
      parent_classes?: ElementClass<'span'>;
      props?: LucideIconProps;
   }
   | {
      source: 'fa';
      rounded_classes?: boolean;
      name: string;
      no_colors?: boolean;
      parent_classes?: ElementClass<'span'>;
      classes?: ElementClass<'div'>;
      props?: FaProps;
   };

type IconSize =
   | 'xs'
   | 'sm'
   | 'lg'
   | '1x'
   | '2x'
   | '3x'
   | '4x'
   | '5x'
   | '6x'
   | '7x'
   | '8x'
   | '9x'
   | '10x'
   | `${number}x`;

type FlipDir = 'horizontal' | 'vertical' | 'both';
type PullDir = 'left' | 'right';

export type FaProps = {
   class?: string | undefined;
   id?: string | undefined;
   style?: string | undefined;
   title?: string | undefined;
   size?: IconSize | undefined;
   color?: string | undefined;
   fw?: boolean | undefined;
   pull?: PullDir | undefined;
   scale?: string | number | undefined;
   translateX?: string | number | undefined;
   translateY?: string | number | undefined;
   rotate?: number | string | undefined;
   flip?: FlipDir | undefined;
   spin?: boolean | undefined;
   pulse?: boolean | undefined;
   primaryColor?: string | undefined;
   secondaryColor?: string | undefined;
   primaryOpacity?: string | number | undefined;
   secondaryOpacity?: string | number | undefined;
   swapOpacity?: boolean | undefined;
};

export type ElementType<T extends keyof SvelteHTMLElements> = SvelteHTMLElements[T];
export type ElementClass<T extends keyof SvelteHTMLElements> = ElementType<T>['class'];
export type PaginatedT<T> = T & { $order_number: number };

export type TableProps = {
   table: {
      classes?: string;
      rounded_classes?: string;
      body_classes?: string;
      table_classes?: string;
      table_styles?: string;
      direction?: 'center' | 'right' | 'left';
      headers?: Snippet<[Component<TableProps['header']>]>;
      rows: Snippet<[Component<TableProps['row']>, Component<TableProps['cell']>]>;
   };

   header: {
      header: string;
      shrink?: boolean;
      fit?: boolean;
      disable_translation?: boolean;
      classes?: ElementType<'div'>['class'];
      children?: Snippet;
      tooltip?: string;
   };

   row: {
      children?: Snippet;
      classes?: ElementType<'div'>['class'];
      id?: string;
   } & ElementType<'tr'>;

   cell: {
      children?: Snippet;
      shrink?: boolean;
      fit?: boolean;
      cursor?: string;
      classes?: ElementType<'div'>['class'];
      separator?: boolean;
      tooltip?: string;
   } & ElementType<'td'>;
};

export type PaginatedTableSchema<T> = Partial<{
   [K in keyof T]: {
      text?: any;
      html?: string;
      text_fn?: (item: T) => any;
      html_fn?: (item: T) => string;
      snippet_fn?: Snippet<[T]>;
      header_props?: TableProps['header'];
      header_classes?: ElementType<'div'>['class'];
      header_snippet?: Snippet;
      cell_classes_fn?: (item: T) => ElementType<'div'>['class'];
      shrink?: boolean;
      label?: string;
      fit?: boolean;
      cell_props?: TableProps['cell'];
      cell_props_fn?: (item: T) => TableProps['cell'];
   };
}> & {
   [key: string]: {
      text?: any;
      html?: string;
      text_fn?: (item: T) => any;
      html_fn?: (item: T) => string;
      snippet_fn?: Snippet<[T]>;
      header_props?: TableProps['header'];
      header_classes?: ElementType<'div'>['class'];
      header_snippet?: Snippet;
      cell_classes_fn?: (item: T) => ElementType<'div'>['class'];
      shrink?: boolean;
      label?: string;
      fit?: boolean;
      cell_props?: TableProps['cell'];
      cell_props_fn?: (item: T) => TableProps['cell'];
   };
};

export type ListGridProps = {
   list: {
      title: string | null;
      subtitle?: string | number | null;
      subtitle_class?: string;
      title_class?: string;
      class?: ElementType<'div'>['class'];
      tooltip?: string;
      mode?: 'single' | 'dual';
      show?: boolean;
      props?: ElementType<'div'>;
   }[];
   rounded_classes?: ElementType<'div'>['class'];
   classes?: ElementType<'div'>['class'];
   block_classes?: ElementType<'div'>['class'];
};

type UpdateOrAppend<T> = { array: T[]; object: T; condition: (obj: T) => boolean };
type UpdateOrAppendUsingID<T> = { array: T[]; object: T };
type UpdateOrAppendRuneUsingID<T> = { rune: { value: any }; object: T };
type AnyObject = Record<string, any>;

export type TimeValue = { hours: number; minutes: number };
export type TimeRange = { start: TimeValue; end: TimeValue };
export type BaseWindow = { include: TimeRange[]; exclude: TimeRange[] };
export type DayKey = 0 | 1 | 2 | 3 | 4 | 5 | 6;

export type GlobalWindow = { type: 'global' } & BaseWindow;
export type WeeklyWindow = { type: 'weekly'; days: Partial<{ [key in DayKey]: BaseWindow }> };
export type DateWindow = { type: 'date'; date: string } & BaseWindow;
export type TimeWindow = GlobalWindow | WeeklyWindow | DateWindow;
export type WindowsConfig = { global: BaseWindow; weekly: WeeklyWindow['days']; dates: DateWindow[] };

function cn(...inputs: ClassValue[]) {
   return twMerge(clsx(inputs));
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type WithoutChild<T> = T extends { child?: any } ? Omit<T, 'child'> : T;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type WithoutChildren<T> = T extends { children?: any } ? Omit<T, 'children'> : T;
export type WithoutChildrenOrChild<T> = WithoutChildren<WithoutChild<T>>;
export type WithElementRef<T, U extends HTMLElement = HTMLElement> = T & { ref?: U | null };

const is_browser = !(typeof window === 'undefined');

function lazy_load_script(url: string, on_load_fn?: () => void) {
   return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = url;
      script.onload = () => {
         if (on_load_fn) on_load_fn();
         resolve(true);
      };
      script.onerror = reject;
      document.body.appendChild(script);
   });
}



const encryptions = {
   crypto_module: null as typeof globalThis.CryptoJS | null,
   async import_crypto_module() {
      const CryptoJS = await import('crypto-js');
      encryptions.crypto_module = CryptoJS;
   },
   ENCRYPTION_KEY: import.meta.env.VITE_ENCRYPTION_KEY,
   async encrypt_string(text: string): Promise<string> {
      if (!encryptions.crypto_module) await encryptions.import_crypto_module();
      if (!encryptions.crypto_module) return '';

      return encodeURIComponent(
         encryptions.crypto_module.AES.encrypt(text, encryptions.ENCRYPTION_KEY).toString()
      );
   }
};

const forms = {
   formdata_to_object(target: HTMLFormElement) {
      const raw_form_data = new FormData(target);
      const result: { [key: string]: string | undefined } = {};

      raw_form_data.forEach((value, key) => {
         // @ts-ignore 
         result[key] = value;
      });

      return result;
   },

   formdata_event_to_object(e: any) {
      const target = e.target as HTMLFormElement;
      const raw_form_data = new FormData(target);
      const result: { [key: string]: any } = {};

      raw_form_data.forEach((value, key) => {
         result[key] = value;
      });

      return result;
   }
};

const text_fns = {
   snake_case_to_title(s: string): string {
      return s.replace(/^_*(.)|_+(.)/g, (s, c, d) => (c ? c.toUpperCase() : ' ' + d.toUpperCase()));
   },
   string_to_snake_case(str: string): string {
      if (!str) return '';
      return (
         str
            .match(/[A-Z]{2,}(?=[A-Z][a-z]+[0-9]*|\b)|[A-Z]?[a-z]+[0-9]*|[A-Z]|[0-9]+/g)
            ?.map((x) => x.toLowerCase())
            .join('_') ?? ''
      );
   },
   characters: 'abcdefghijklmnopqrstuvwxyz',
   random_string(length = 10) {
      let result = '';
      const charactersLength = text_fns.characters.length;
      for (let i = 0; i < length; i++) {
         result += text_fns.characters.charAt(Math.floor(Math.random() * charactersLength));
      }
      return result;
   },

   random_password_string(length = 10) {
      let result = '';
      const charactersLength = text_fns.characters.length;
      for (let i = 0; i < length; i++) {
         result += text_fns.characters.charAt(Math.floor(Math.random() * charactersLength));
      }
      return result;
   },
   generate_uuid() {
      return String('xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx').replace(/[xy]/g, (character) => {
         const random = (Math.random() * 16) | 0;
         const value = character === 'x' ? random : (random & 0x3) | 0x8;

         return value.toString(16);
      });
   }
};

const jwt_utils = {
   decode_token_role: (token: string | null, role: Scalars['user_role']) => {
      try {
         if (!token || typeof token !== 'string') return null;
         const array_token = token.split('.');
         const token_payload = JSON.parse(atob(array_token[1])) as any;

         const data: {
            role: Scalars['user_role'];
            user_id: string;
            iat: Date;
            exp: Date;
         } = {
            role: token_payload['https://hasura.io/jwt/claims']['x-hasura-default-role'],
            user_id: token_payload['https://hasura.io/jwt/claims']['x-hasura-user-id'],
            iat: new Date(token_payload.iat),
            exp: new Date(token_payload.exp)
         };

         if (!object_fns.all_keys_values(data) || data.role !== role) return null;
         return data;
      } catch (error) {
         console.error({ error });
         return null;
      }
   },
   decode_token: (token: string | null, options?: { validate_expiry: boolean }) => {
      try {
         if (!token || typeof token !== 'string') return null;
         const array_token = token.split('.');
         const token_payload = JSON.parse(atob(array_token[1])) as any;

         const data: {
            role: Scalars['user_role'];
            user_id: string;
            iat: Date;
            exp: Date;
         } = {
            role: token_payload['https://hasura.io/jwt/claims']['x-hasura-default-role'],
            user_id: token_payload['https://hasura.io/jwt/claims']['x-hasura-user-id'],
            iat: new Date(token_payload.iat),
            exp: new Date(token_payload.exp)
         };

         if (!object_fns.all_keys_values(data)) return null;
         if (options?.validate_expiry && Date.now() > token_payload.exp * 1000) return null;
         return data;
      } catch (error) {
         console.error({ error });
         return null;
      }
   }
};

const object_fns = {
   filter_unchanged<T extends object>({
      source,
      updates
   }: {
      source: T;
      updates: Partial<T>;
   }): Partial<T> | undefined {
      const updated_values: Partial<T> = {};

      for (const key in updates) {
         if (updates.hasOwnProperty(key) && source.hasOwnProperty(key)) {
            if (updates[key] !== null && updates[key] !== undefined && updates[key] !== source[key]) {
               updated_values[key] = updates[key];
            }
         }
      }
      if (!Object.keys(updated_values).length) return undefined;
      return updated_values;
   },
   all_keys_values<T extends object>(object: T): boolean {
      return Object.keys(object).length &&
         Object.entries(object).every(([key, value]) => value !== null && value !== undefined)
         ? true
         : false;
   },
   remove_nulls_from_object<T extends Record<string, any>>(object: T): Partial<T> {
      return Object.fromEntries(Object.entries(object).filter(([_, v]) => v !== null)) as Partial<T>;
   },
   remove_undefined_values_from_object<T extends Record<string, any>>(object: T): Partial<T> {
      return Object.fromEntries(
         Object.entries(object).filter(([_, v]) => v !== undefined)
      ) as Partial<T>;
   },
   deep_copy<T>(object: T): T {
      return !object ? object : (JSON.parse(JSON.stringify(object)) as T);
   },
   filter_objects_by_keys<T extends Record<string, any>>(object: T, keys: string[]): Partial<T> {
      return Object.fromEntries(
         Object.entries(object).filter(([key]) => keys.includes(key))
      ) as Partial<T>;
   },

   // are_objects_equal<T extends object>(obj1: T, obj2: T) {
   //    const keys1 = Object.keys(obj1);
   //    const keys2 = Object.keys(obj2);

   //    if (keys1.length !== keys2.length) {
   //       return false;
   //    }

   //    for (let key of keys1) {
   //       if ((obj1 as any)[key] !== (obj2 as any)[key]) {
   //          return false;
   //       }
   //    }
   //    return true;
   // }

};

const dates = {
   add_days_to_date(date: Date, days: number) {
      const result = new Date(date);
      result.setDate(result.getDate() + days);
      return result;
   },

   create_svelte_date_timer() {
      let date = new SvelteDate();

      const interval = setInterval(() => {
         date.setTime(Date.now());
      }, 1000);

      // Cleanup the interval when the component is destroyed
      return {
         get date() { return date },
         get date_time() { return date.getTime() },
         unsubscribe: () => {
            clearInterval(interval);
         }
      };

   },


   convert_date_to_local(date_string: string) {
      const date = new Date(date_string);
      date.toLocaleTimeString();
      return date;
   },

   add_minutes_to_date(date: Date | string, minutes: number) {
      try {
         const result = new Date(date);

         result.setMinutes(result.getMinutes() + minutes);
         return result;
      } catch (error) {
         return '';
      }
   },

   is_date(date: Date | any): date is Date {
      return (
         date &&
         date instanceof Date &&
         (date as unknown as string) !== 'Invalid Date' &&
         !isNaN(date.getTime())
      );
   },

   confirm_date(input: Date | any): Date | null {
      try {
         const date = new Date(input);
         if (!(date instanceof Date) || date.toString() === 'Invalid Date') return null;
         return date;
      } catch (error) {
         return null;
      }
   },

   time_elapsed(timestamp: number | undefined | string | Date) {
      if (typeof timestamp !== 'number' && typeof timestamp !== 'string') return '';
      if (typeof timestamp !== 'number') timestamp = new Date(timestamp).getTime();

      try {
         const now = Date.now();
         const elapsed = now - timestamp;
         const elapsed_seconds = Math.round(elapsed / 1000);
         const since_translation = Loc('since');

         if (now && elapsed_seconds) {
            if (elapsed_seconds < 60) {
               return `${since_translation} ${elapsed_seconds} ${Loc(
                  elapsed_seconds > 2 && elapsed_seconds < 11 ? 'seconds' : 'second'
               )}`;
            } else if (elapsed_seconds < 3600) {
               const elapsed_minutes = Math.round(elapsed_seconds / 60);
               return `${since_translation} ${elapsed_minutes > 1 ? elapsed_minutes : ''} ${Loc(
                  elapsed_minutes > 2 && elapsed_minutes < 11 ? 'minutes' : 'minute'
               )}`;
            } else if (elapsed_seconds < 86400) {
               const elapsed_hours = Math.round(elapsed_seconds / 3600);
               return `${since_translation} ${elapsed_hours > 1 ? elapsed_hours : ''} ${Loc(
                  elapsed_hours > 2 && elapsed_hours < 11 ? 'hours' : 'hour'
               )}`;
            } else if (elapsed_seconds < 604800) {
               const elapsed_days = Math.round(elapsed_seconds / 86400);
               return `${since_translation} ${elapsed_days > 1 ? elapsed_days : ''} ${Loc(
                  elapsed_days > 2 && elapsed_days < 11 ? 'days' : 'day'
               )}`;
            } else if (elapsed_seconds < 2592000) {
               const elapsed_weeks = Math.round(elapsed_seconds / 604800);
               return `${since_translation} ${elapsed_weeks > 1 ? elapsed_weeks : ''} ${Loc(
                  elapsed_weeks > 2 && elapsed_weeks < 11 ? 'weeks' : 'week'
               )}`;
            } else if (elapsed_seconds < 31536000) {
               const elapsed_months = Math.round(elapsed_seconds / 2592000);
               return `${since_translation} ${elapsed_months > 1 ? elapsed_months : ''} ${Loc(
                  elapsed_months > 2 && elapsed_months < 11 ? 'months' : 'month'
               )}`;
            } else {
               const elapsed_years = Math.round(elapsed_seconds / 31536000);
               return `${since_translation} ${elapsed_years > 1 ? elapsed_years : ''} ${Loc(
                  elapsed_years > 2 && elapsed_years < 11 ? 'years' : 'year'
               )}`;
            }
         }
      } catch (error) {
         console.log('error at time_elapsed fn in unit_conversions');
         return '';
      }
      return '';
   },

   display_time(time_in_seconds: number): string {
      if (!time_in_seconds) return '';

      time_in_seconds = Math.ceil(Math.abs(time_in_seconds));
      const years = Math.floor(time_in_seconds / (3600 * 24 * 365));
      time_in_seconds -= years * (3600 * 24 * 365);

      const months = Math.floor(time_in_seconds / (3600 * 24 * 30));
      time_in_seconds -= months * (3600 * 24 * 30);

      const days = Math.floor(time_in_seconds / (3600 * 24));
      time_in_seconds -= days * (3600 * 24);

      const hours = Math.floor(time_in_seconds / 3600);
      time_in_seconds -= hours * 3600;

      const minutes = Math.floor(time_in_seconds / 60);
      const seconds = time_in_seconds % 60;

      const intervals: string[] = [];

      if (years > 0) {
         intervals.push(`${years} ${Loc(years > 1 ? 'years' : 'year')}`);
         if (months > 0) {
            intervals.push(`${months} ${Loc(months > 1 ? 'months' : 'month')}`);
         }
      } else if (months > 0) {
         intervals.push(`${months} ${Loc(months > 1 ? 'months' : 'month')}`);
         if (days > 0) {
            intervals.push(`${days} ${Loc(days > 1 ? 'days' : 'day')}`);
         }
      } else if (days > 0) {
         intervals.push(`${days} ${Loc(days > 1 ? 'days' : 'day')}`);
         if (hours > 0) {
            intervals.push(`${hours} ${Loc(hours > 1 ? 'hours' : 'hour')}`);
         }
      } else if (hours > 0) {
         intervals.push(`${hours} ${Loc(hours > 1 ? 'hours' : 'hour')}`);
         if (minutes > 0) {
            intervals.push(`${minutes} ${Loc(minutes > 1 ? 'minutes' : 'minute')}`);
         }
      } else if (minutes > 0) {
         intervals.push(`${minutes} ${Loc(minutes > 1 ? 'minutes' : 'minute')}`);
         if (seconds > 0) {
            intervals.push(`${seconds} ${Loc(seconds > 1 ? 'seconds' : 'second')}`);
         }
      } else if (seconds > 0) {
         intervals.push(`${seconds} ${Loc(seconds > 1 ? 'seconds' : 'second')}`);
      }

      if (intervals.length === 0) return '';

      return intervals.join(` ${Loc('and')} `);
   },

   display_distance(input_distance: number, unit: 'km' | 'm' = 'm') {
      try {
         let new_distance = input_distance;
         if (input_distance) {
            if (unit === 'km') {
               new_distance = input_distance * 1000;
            } else if (unit === 'm') {
               new_distance = input_distance;
            }
            if (new_distance < 1000) {
               return `${new_distance?.toFixed(1)} ${Loc('metres')}`;
            } else {
               return `${(new_distance / 1000)?.toFixed(2)} ${Loc('km')}`;
            }
         }
      } catch (error) {
         console.log('error at display_distance fn in unit_conversions');
      }
   },

   display_date_time(date_string?: string | Date, options?: { year?: boolean, is_short?: boolean }) {
      const date = dates.confirm_date(date_string);
      if (!date) return Loc('invalid_date');

      return new Intl.DateTimeFormat('ar-EG-u-nu-latn', {
         day: '2-digit',
         year: options?.year ? 'numeric' : undefined,
         weekday: options?.is_short ? undefined : 'long',
         month: '2-digit',
         hour12: false,
         hour: '2-digit',
         minute: '2-digit',
         timeZone: 'Africa/Tripoli'
      }).format(date);
   },

   display_date_time_modular(
      date_string?: string | Date,
      options?: {
         year?: boolean;
         day?: boolean;
         weekday?: boolean;
         month?: boolean;
         hour?: boolean;
         minute?: boolean;
         is_short?: boolean;
      }
   ) {
      const date = dates.confirm_date(date_string);
      if (!date) return Loc('invalid_date');
      const short_or_long = options?.is_short ? '2-digit' : 'long';

      return new Intl.DateTimeFormat('ar-EG-u-nu-latn', {
         day: options?.day ? '2-digit' : undefined,
         year: options?.year ? 'numeric' : undefined,
         weekday: options?.weekday ? 'long' : undefined,
         month: options?.month ? short_or_long : undefined,
         hour12: false,
         hour: options?.hour ? '2-digit' : undefined,
         minute: options?.minute ? '2-digit' : undefined,
         timeZone: 'Africa/Tripoli'
      }).format(date);
   },

   display_date_2_lines(date_string?: string | Date): string[] {
      if (!date_string) return ['', ''];
      const date = dates.confirm_date(date_string);
      if (!date) return ['', ''];

      const formatted_date = new Intl.DateTimeFormat('ar-EG-u-nu-latn', {
         month: 'long',
         day: '2-digit',
         weekday: 'long',
         timeZone: 'Africa/Tripoli'
      })
         .format(date)
         .split('،');
      return formatted_date;
   },

   display_hour_minute(input: Date | string | undefined | null) {
      const date = dates.confirm_date(input);
      if (!date) return Loc('invalid_date');

      return new Intl.DateTimeFormat('ar-EG-u-nu-latn', {
         hour: '2-digit',
         minute: '2-digit',
         hour12: false,
         timeZone: 'Africa/Tripoli'
      }).format(date);
   },

   display_hour(_hour: string) {
      const hour = +_hour;

      if (hour < 13) return hour + Loc('am');
      else {
         const pm_hour = hour - 12;
         return pm_hour + Loc('pm');
      }
   },

   from_today_locale_date_string(offset = 0) {
      const today = new Date();
      today.setDate(today.getDate() + offset);

      const return_date = `${today.getFullYear()}-${(today.getMonth() + 1).toString().padStart(2, '0')}-${today.getDate().toString().padStart(2, '0')}`;
      return return_date;
   },

   convert_local_time_to_utc(time_string: string) {
      // Parse the time string
      const [hours, minutes, seconds = '00'] = time_string.split(':').map(Number);

      // Get today's date in local time
      const localDate = new Date();
      localDate.setHours(hours);
      localDate.setMinutes(minutes);
      localDate.setSeconds(+seconds);
      localDate.setMilliseconds(0);

      // Convert to UTC
      const utcDate = new Date(localDate.toUTCString());

      return utcDate.toLocaleTimeString('en-US', {
         hour: '2-digit',
         minute: '2-digit',
         second: '2-digit',
         timeZone: 'UTC'
      });
   },

   convert_utc_to_local_time(utc_time_string: string) {
      // Split the UTC time string into hours, minutes, and seconds
      const [hours, minutes, seconds = 0] = utc_time_string.split(':').map(Number);

      // Create a Date object with the UTC time
      const date = new Date();
      date.setUTCHours(hours, minutes, seconds, 0); // Set time in UTC

      // Format the date to the local time (hours, minutes, and seconds only)
      const localTime = new Intl.DateTimeFormat('default', {
         hour: '2-digit',
         minute: '2-digit',
         second: '2-digit',
         hour12: false,
         timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
      }).format(date);

      return localTime;
   },

   latest_date_in_array<T, K extends keyof T>(array: T[], key: K) {
      try {
         if (!array?.length) return null;

         const latest_timestamp = array.reduce((latest, item) => {
            const item_timestamp = Date.parse(item[key] as string); // Direct timestamp parsing
            return item_timestamp > latest ? item_timestamp : latest;
         }, 0); // Initialize with a very old timestamp
         // console.log({ latest_timestamp })
         const latest_date = latest_timestamp > 0 ? new Date(latest_timestamp) : null; // Convert back to Date object if needed

         return latest_date;
      } catch (error) {
         return null;
      }
   },

   object_with_latest_date_in_array<T, K extends keyof T>(array: T[], key: K): T | null {
      try {
         if (!array?.length) return null;

         // console.log({ array })
         const latest_item = array.reduce(
            (latest, item) => {
               const item_timestamp = Date.parse(item[key] as string); // Parse the date string to timestamp
               const latest_timestamp = latest ? Date.parse(latest[key] as string) : -Infinity;
               return item_timestamp > latest_timestamp ? item : latest;
            },
            null as T | null
         ); // Start with null as the initial value

         if (!latest_item?.[key]) return null;

         return dates.is_date(new Date(latest_item[key] as string)) ? latest_item : null;
      } catch (error) {
         return null;
      }
   },
   time_windows: {
      /**
       * Converts a UTC TimeValue to a Display TimeValue (UTC+2).
       */
      time_utc_to_display(time_utc: TimeValue, offset_hours: number = 2): TimeValue {
         const date = new Date(Date.UTC(1970, 0, 1, time_utc.hours, time_utc.minutes));
         date.setUTCHours(date.getUTCHours() + offset_hours);
         return { hours: date.getUTCHours(), minutes: date.getUTCMinutes() };
      },

      /**
       * Converts a Display TimeValue (UTC+2) to a UTC TimeValue.
       */
      time_display_to_utc(time_display: TimeValue, offset_hours: number = 2): TimeValue {
         const date = new Date(Date.UTC(1970, 0, 1, time_display.hours, time_display.minutes));
         date.setUTCHours(date.getUTCHours() - offset_hours);
         return { hours: date.getUTCHours(), minutes: date.getUTCMinutes() };
      },

      /**
       * Converts a UTC TimeRange to a Display TimeRange (UTC+2).
       */
      range_utc_to_display(r_utc: TimeRange): TimeRange {
         return {
            start: this.time_utc_to_display(r_utc.start),
            end: this.time_utc_to_display(r_utc.end)
         };
      },

      /**
       * Converts a Display TimeRange (UTC+2) to a UTC TimeRange.
       */
      range_display_to_utc(r_display: TimeRange): TimeRange {
         return {
            start: this.time_display_to_utc(r_display.start),
            end: this.time_display_to_utc(r_display.end)
         };
      },

      // --- NEW: Centralized Validation ---
      /**
       * Checks if a time range is valid (end > start).
       * This check works on any timezone, as long as both values are in the same one (e.g., both UTC).
       */
      is_range_valid(range: TimeRange): boolean {
         const start_minutes = range.start.hours * 60 + range.start.minutes;
         const end_minutes = range.end.hours * 60 + range.end.minutes;
         return end_minutes > start_minutes;
      },

      // --- NEW: Centralized Formatting (Converts UTC to Display) ---

      /**
       * Formats a single UTC TimeValue into a UTC+2 string.
       */
      format_display_time(time_utc: TimeValue): string {
         const display_time = this.time_utc_to_display(time_utc);
         return `${display_time.hours.toString().padStart(2, '0')}:${display_time.minutes.toString().padStart(2, '0')}`;
      },

      /**
       * Formats a UTC TimeRange into a UTC+2 string.
       */
      format_display_range(range_utc: TimeRange, loc_from: string, loc_to: string): string {
         return `${loc_from} ${this.format_display_time(range_utc.start)} - ${loc_to} ${this.format_display_time(range_utc.end)}`;
      },

      // --- Existing Functions (Unchanged, but now operate on UTC data) ---

      // Format date for display
      format_date(date_str: string): string {
         const date = new Date(date_str + 'T00:00:00'); // Add time to avoid timezone issues
         return date.toLocaleDateString('ar-EG-u-nu-latn', {
            weekday: 'short',
            year: 'numeric',
            month: 'short',
            day: 'numeric'
         });
      },

      are_ranges_equal(a: TimeRange, b: TimeRange) {
         return (
            a.start.hours === b.start.hours &&
            a.start.minutes === b.start.minutes &&
            a.end.hours === b.end.hours &&
            a.end.minutes === b.end.minutes
         );
      },
      add_window(target_window: BaseWindow, type: 'include' | 'exclude', range: TimeRange) {
         // 1. Check for duplicates in the same list (No change here)
         const is_duplicate = target_window[type].some((r) => this.are_ranges_equal(r, range));
         if (is_duplicate) {
            toast.error('time_range_exists');
            return false;
         }

         // 2. NEW: Check for conflicts in the opposite list
         const opposite_type = type === 'include' ? 'exclude' : 'include';
         const is_conflict = target_window[opposite_type].some((r) => this.are_ranges_equal(r, range));
         if (is_conflict) {
            // You might want a more specific error message here
            toast.error('time_range_conflict'); // e.g., "This range is already in the 'exclude' list."
            return false;
         }

         // 3. Add the window if no duplicate or conflict is found
         target_window[type].push(range);
         target_window[type] = target_window[type].sort(
            (a, b) => a.start.hours * 60 + a.start.minutes - (b.start.hours * 60 + b.start.minutes)
         );
         return true;
      },

      remove_window(target_window: BaseWindow, type: 'include' | 'exclude', index: number) {
         target_window[type].splice(index, 1);
         return true;
      },

      remove_specific_date_window(specific_dates_windows: DateWindow[], index: number) {
         specific_dates_windows.splice(index, 1);
         return true;
      },

      week_days: [
         { key: 0, label: Loc('sunday') },
         { key: 1, label: Loc('monday') },
         { key: 2, label: Loc('tuesday') },
         { key: 3, label: Loc('wednesday') },
         { key: 4, label: Loc('thursday') },
         { key: 5, label: Loc('friday') },
         { key: 6, label: Loc('saturday') }
      ] as { key: DayKey; label: string }[]
   }
   // time_windows: {
   //    to_local_time({ hours, minutes }: TimeValue): TimeValue {
   //       const d = new Date(Date.UTC(1970, 0, 1, hours, minutes));
   //       return { hours: d.getHours(), minutes: d.getMinutes() };
   //    },
   //    to_utc_time({ hours, minutes }: TimeValue): TimeValue {
   //       const d = new Date(1970, 0, 1, hours, minutes);
   //       return { hours: d.getUTCHours(), minutes: d.getUTCMinutes() };
   //    },
   //    range_to_local(r: TimeRange): TimeRange {
   //       return {
   //          start: this.to_local_time(r.start),
   //          end: this.to_local_time(r.end)
   //       };
   //    },
   //    range_to_utc(r: TimeRange): TimeRange {
   //       return {
   //          start: this.to_utc_time(r.start),
   //          end: this.to_utc_time(r.end)
   //       };
   //    },
   //    // Format date for display (YYYY-MM-DD to more readable format)
   //    format_date(date_str: string): string {
   //       const date = new Date(date_str + 'T00:00:00'); // Add time to avoid timezone issues
   //       return date.toLocaleDateString(undefined, {
   //          weekday: 'short',
   //          year: 'numeric',
   //          month: 'short',
   //          day: 'numeric'
   //       });
   //    },

   //    are_ranges_equal(a: TimeRange, b: TimeRange) {
   //       return (
   //          a.start.hours === b.start.hours &&
   //          a.start.minutes === b.start.minutes &&
   //          a.end.hours === b.end.hours &&
   //          a.end.minutes === b.end.minutes
   //       );
   //    },
   //    add_window(target_window: BaseWindow, type: 'include' | 'exclude', range: TimeRange) {
   //       // Check for duplicates in the same window
   //       const is_duplicate = target_window[type].some((r) => this.are_ranges_equal(r, range));
   //       if (is_duplicate) {
   //          toast.error('time_range_exists');
   //          return false;
   //       }

   //       target_window[type].push(range);
   //       target_window[type] = target_window[type].sort(
   //          (a, b) => a.start.hours * 60 + a.start.minutes - (b.start.hours * 60 + b.start.minutes)
   //       );
   //       return true;
   //    },
   //    remove_window(target_window: BaseWindow, type: 'include' | 'exclude', index: number) {
   //       target_window[type].splice(index, 1);
   //       return true
   //    },
   //    remove_specific_date_window(specific_dates_windows: DateWindow[], index: number) {
   //       specific_dates_windows.splice(index, 1);
   //       return true
   //    },
   //    week_days: [
   //       { key: 0, label: Loc('sunday') },
   //       { key: 1, label: Loc('monday') },
   //       { key: 2, label: Loc('tuesday') },
   //       { key: 3, label: Loc('wednesday') },
   //       { key: 4, label: Loc('thursday') },
   //       { key: 5, label: Loc('friday') },
   //       { key: 6, label: Loc('saturday') }
   //    ] as { key: DayKey; label: string }[]

   // },
};

const array_fns = {
   is_valid<T>(input: any[] | null | undefined | T): input is any[] {
      if (!input || !Array.isArray(input) || !input.length || !input?.[0]) return false;
      return true;
   },

   key_numeric_value_aggregate<T>(array: T[], key: keyof T): number {
      return array.reduce((prev, cur) => {
         if (typeof cur[key] !== 'number') return prev
         return prev + cur[key]
      }, 0)
   },

   remove_nulls_from_object<T extends Record<string, any>>(object: T): Partial<T> {
      return Object.fromEntries(Object.entries(object).filter(([_, v]) => v !== null)) as Partial<T>;
   },

   split_array_2_objects<T extends any[]>(
      array: T,
      validation_function: (item: T[number]) => boolean
   ) {
      let valid_item: T[number] | null = null;
      let invalid_item: T[number] | null = null;

      for (const item of array) {
         if (validation_function(item)) valid_item = item;
         else invalid_item = item;
      }

      return {
         valid_item,
         invalid_item
      };
   },

   update_or_append<T>({ array, object, condition }: UpdateOrAppend<T>): T[] {
      let found = false;

      for (let i = 0; i < array.length; i++) {
         if (condition(array[i])) {
            array[i] = object;
            found = true;
            break;
         }
      }

      if (!found) array.push(object);
      return array;
   },

   update_or_append_using_id<T>({ array, object }: UpdateOrAppendUsingID<T>): T[] {
      let found = false;
      type TID = T & { id: string };
      for (let i = 0; i < array.length; i++) {
         if ((array[i] as TID)?.id === (object as TID)?.id) {
            array[i] = object;
            found = true;
            break;
         }
      }

      if (!found) array.push(object);
      return array;
   },

   update_or_append_rune_using_id<T>({ rune, object }: UpdateOrAppendRuneUsingID<T>): void {
      const rune_length = rune.value.length;
      type TID = T & { id: string };

      for (let i = 0; i < rune_length; i++) {
         if ((rune.value[i] as TID)?.id === (object as TID)?.id) {
            rune.value[i] = object;
            return;
         }
      }
      console.log('NOT FOUND... PUSHING');
      rune.value.push(object);
   },

   are_objects_equal(object1: any, object2: any): boolean {
      if (object1 instanceof File !== object2 instanceof File) return false;

      if (typeof object1 !== typeof object2) return false;
      if (typeof object1 === 'function') return false;
      if (
         typeof object1 === 'string' ||
         typeof object1 === 'number' ||
         typeof object1 === 'boolean' ||
         typeof object1 === 'undefined' ||
         object1 === null
      ) {
         return object1 === object2;
      }

      if (Array.isArray(object1)) {
         if (!Array.isArray(object2)) return false;
         if (object1.length !== object2.length) return false;
         return object1.every((value, index) => array_fns.are_objects_equal(value, object2[index]));
      }

      const object1Keys = Object.keys(object1);
      const object2Keys = Object.keys(object2);

      if (object1Keys.length !== object2Keys.length) return false;

      return object1Keys.every((key) => {
         if (!(key in object2)) return false;

         const value1 = object1[key];
         const value2 = object2[key];

         if (typeof value1 !== typeof value2) return false;
         if (
            typeof value1 === 'string' ||
            typeof value1 === 'number' ||
            typeof value1 === 'boolean' ||
            typeof value1 === 'undefined' ||
            value1 === null
         ) {
            return value1 === value2;
         }

         if (Array.isArray(value1)) {
            if (!Array.isArray(value2)) return false;
            if (value1.length !== value2.length) return false;
            return value1.every((value, index) =>
               array_fns.are_objects_equal(value, object2[key][index])
            );
         }

         if (typeof value1 === 'object' && value1 !== null) {
            return array_fns.are_objects_equal(value1, value2);
         }
      });
   },
   sort_array_by_date<T>({
      array,
      key,
      order
   }: {
      array: T[];
      key: keyof T;
      order: 'asc' | 'desc';
   }) {
      // @ts-ignore
      if (order === 'asc') return array.sort((a, b) => Date.parse(a[key]) - Date.parse(b[key]));
      // @ts-ignore
      else return array.sort((a, b) => Date.parse(b[key]) - Date.parse(a[key]));
   },

   are_arrays_equal(array1: any[] | undefined, array2: any[] | undefined): boolean {
      if (!array1 || !array2) return false;
      if (array1.length !== array2.length) return false;

      return array1.every((value, index) => {
         if (typeof value !== typeof array2[index]) return false;
         if (
            typeof value === 'string' ||
            typeof value === 'number' ||
            typeof value === 'boolean' ||
            typeof value === 'undefined' ||
            value === null
         ) {
            return value === array2[index];
         }

         if (Array.isArray(value)) {
            if (!Array.isArray(array2[index])) return false;
            return array_fns.are_arrays_equal(value, array2[index]);
         }

         if (typeof value === 'object' && value !== null) {
            return array_fns.are_objects_equal(value, array2[index]);
         }

         return true;
      });
   },

   deep_merge<T extends AnyObject = AnyObject, U extends AnyObject = AnyObject>(
      source: T,
      target: U
   ): T & U {
      // @ts-ignore
      return (
         // @ts-ignore
         void Object.keys(target).forEach((key) => {
            source[key] instanceof Object && target[key] instanceof Object
               ? source[key] instanceof Array && target[key] instanceof Array
                  ? // @ts-ignore
                  void (source[key] = Array.from(new Set(source[key].concat(target[key]))))
                  : !(source[key] instanceof Array) && !(target[key] instanceof Array)
                     ? void array_fns.deep_merge(source[key], target[key])
                     : // @ts-ignore
                     void (source[key] = target[key])
               : // @ts-ignore
               void (source[key] = target[key]);
         }) || source
      );
   }
};

const numbers = {
   number_to_decimals(number?: number | string, decimals = 2) {
      if (!number) return (0).toFixed(decimals);
      const numeric_number = isFinite(+number) ? +number : 0;
      return numeric_number ? numeric_number.toFixed(decimals) : (0).toFixed(decimals);
   },
   parse_libyan_phone(raw_phone_text: string): { phone?: string; error?: string } {
      if (!raw_phone_text) return { error: 'empty' };

      // 1. Strip all spaces, dashes, parentheses, and dots
      let cleaned = raw_phone_text.replace(/[\s\-().]/g, '');

      // 2. Check for international prefixes
      const has_plus = cleaned.startsWith('+');
      const has_zero_zero = cleaned.startsWith('00');

      if (has_plus || has_zero_zero) {
         const is_libyan = has_plus ? cleaned.startsWith('+218') : cleaned.startsWith('00218');

         if (!is_libyan) {
            return { error: 'error_not_libyan_number' };
         }

         // Strip the country code prefix
         cleaned = cleaned.substring(has_plus ? 4 : 5);
      }

      // 3. Normalize local prefix (if they omitted the leading zero but included the 9)
      if (cleaned.startsWith('9') && cleaned.length === 9) {
         cleaned = '0' + cleaned;
      }

      // 4. Final validation: Must start with 09 and be exactly 10 digits
      // Strip any remaining non-digit characters just in case
      cleaned = cleaned.replace(/\D/g, '');

      if (/^09\d{8}$/.test(cleaned)) {
         return { phone: cleaned };
      }

      return { error: 'invalid_phone_number_format' };
   },
   rounded_down_currency(number: number | string = 0) {
      return numbers.number_to_decimals(Math.floor(+number * 4) / 4);
   }
};

const files = {
   async convert_file_to_base64(file: File) {
      return new Promise<string | null>((resolve) => {
         const reader = new FileReader();
         reader.onerror = () => resolve(null);
         reader.readAsDataURL(file);
         reader.onload = () => resolve(reader.result as string);
      });
   },
};

const images = {
   async upload_image(image_file: File, onprogress?: (progress: number) => void): Promise<string | null> {
      try {
         if (!image_file) return null;

         const { auth_state } = await import('$lib/features/auth/state/auth_state')
         const access_token = await auth_state.get_access_token();
         if (!access_token) return null


         const formData = new FormData();
         formData.append('file', image_file);

         const API_ENDPOINT = import.meta.env.VITE_SERVICES_SITE_URL + '/public/upload_image';

         return new Promise<string | null>((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            xhr.open('POST', API_ENDPOINT);

            // Do NOT set Content-Type to multipart/form-data manually; 
            // the browser sets it with the boundary automatically when sending FormData.
            xhr.setRequestHeader('Authorization', `Bearer ${access_token}`);

            // Track Upload Progress
            xhr.upload.onprogress = (event) => {
               if (event.lengthComputable && onprogress) {
                  const percentComplete = Math.round((event.loaded / event.total) * 100);
                  onprogress(percentComplete);
               }
            };
            // Handle Response
            xhr.onload = () => {
               if (xhr.status >= 200 && xhr.status < 300) {
                  try {
                     const image_id = (JSON.parse(xhr.responseText) as any)?.file_id;
                     resolve(image_id || null);
                  } catch (e) {
                     reject(e);
                  }
               } else {
                  reject(new Error(xhr.statusText));
               }
            };

            xhr.onerror = () => reject(new Error('Network Error'));

            xhr.send(formData);
         })
      } catch (error) {
         return null;
      }
   },

   image_server: import.meta.env.VITE_IMAGE_SERVER,
   get_image_src({
      id = '',
      width,
      height,
      quality = 100
   }: {
      id?: string;
      width: string | number;
      height: string | number;
      quality: number | string;
   }) {
      if (!id) return '';
      return `${images.image_server}/${width}x${height}/filters:quality(${quality}):format(jpeg)/${id}`;
   }
};

const share_handlers = {
   async share(options: { base64_string: string, file_name: string, title: string, text: string }) {
      try {
         if (cap_utils.device.is_device_native()) return await share_handlers.native_share(options);
         else return await share_handlers.web_share(options);
      } catch (error) {
         console.error('Error sharing QR code:', error);
         return false
      }
   },

   async web_share(options: { base64_string: string, file_name: string, title: string, text: string }) {
      const { base64_string, file_name, title, text } = options

      const file = new File([new Blob([base64_string])], file_name, { type: 'image/png' });
      if (!file) return false

      if (!navigator.canShare || !navigator.canShare({ files: [file] }))
         return false

      await navigator.share({
         title,
         text,
         files: [file]
      });
      return true
   },

   async native_share(options: { base64_string: string, file_name: string, title: string, text: string }) {
      try {
         const { base64_string, file_name, title, text } = options

         const file_uri = await cap_utils.filesystem.save_file(file_name, base64_string, 'cache');
         if (!file_uri) return false

         const { Share } = await import('@capacitor/share')


         await Share.share({
            title,
            text,
            files: [file_uri]
         });

         return true
      } catch (error) {
         console.error('Error sharing QR code:', error);
         return false
      }
   },

   async copy_from_clipboard(): Promise<string | null> {
      try {
         // Check if Clipboard API is available :cite[1]:cite[5]
         if (!navigator.clipboard || !navigator.clipboard.readText) {
            throw new Error('Clipboard API not available');
         }

         // Read clipboard text :cite[5]:cite[9]
         return await navigator.clipboard.readText();
      } catch (error) {
         console.error(`Failed to read clipboard: ${error instanceof Error ? error.message : 'Unknown error'}`);
         return null
      }
   },

   async copy_to_clipboard(url: string) {
      try {
         if (!cap_utils.device.is_device_native()) {
            await navigator?.clipboard.writeText(url)
            return true
         };

         const { Clipboard } = await import('@capacitor/clipboard')
         await Clipboard.write({ string: url });
         return true

      } catch (error) {
         console.error('Error sharing QR code:', error);
         return false
      }
   }



}

const prints = {
   paper_sizes_mm: { a2: [420, 594], a3: [297, 420], a4: [210, 297], a5: [148.5, 210] },
   paper_sizes_px: { a2: [1587, 2245], a3: [1123, 1587], a4: [794, 1123], a5: [561, 794] },

   async print_element({
      element,
      element_id,
      format = 'a4',
      styles,
      title = 'Shaggaga Print',
   }: {
      element?: HTMLElement;
      element_id?: string;
      title?: string;
      format?: 'a5' | 'a4' | 'a3';
      styles?: ElementType<'div'>['style'];
   }) {

      const image_data_string = await prints.convert_element_to_image_base64({ element, element_id, format })
      if (!image_data_string) {
         console.error('No image data found')
         return null
      }

      if (cap_utils.device.is_device_native()) {
         const { Printer } = await import('@bcyesil/capacitor-plugin-printer')

         const print_op = await Printer.print({ content: `<img src="${image_data_string}" />` })
         console.error({ print_op });
         return true
      }

      return prints.print_element_web({ element, element_id, format, styles })

   },

   async print_then_share_element(options: { element?: HTMLElement; element_id?: string; file_name: string; share_text: string; title?: string; format?: 'a5' | 'a4' | 'a3'; }) {

      const { element, element_id, format = 'a4', title = 'Shaggaga Print', share_text, file_name } = options

      const image_data_string = await prints.convert_element_to_image_base64({ element, element_id, format })
      if (!image_data_string) {
         console.error('No image data found')
         return null
      }
      return share_handlers.share({ base64_string: image_data_string, file_name, title, text: share_text })
   },

   print_element_web({
      element,
      element_id,
      format = 'a4',
      styles
   }: {
      element?: HTMLElement;
      element_id?: string;
      format?: 'a5' | 'a4' | 'a3';
      styles?: ElementType<'div'>['style'];
   }) {
      // 1) Get the element and its full HTML (including its children)

      const el = element ? element : element_id ? document.getElementById(element_id) : null;
      if (!el) return console.error('No element found');

      const elementHtml = el.outerHTML;

      const marginStyle = styles
         ? `
       <style>
         /* This affects how pages are laid out in print */
         body { ${styles} }
       </style>
     `
         : '';

      const page_html = `
       <html dir="rtl">
         <head>
           ${document.head.innerHTML}
           ${marginStyle}
         </head>
         <body>
           ${elementHtml}
         </body>
       </html>
     `;
      // 2) Open a new, blank window

      const printWindow = window.open(
         '',
         '_blank',
         `width=${prints.paper_sizes_px[format][0]},height=${prints.paper_sizes_px[format][1]}`
      );
      if (!printWindow) return;

      // 3) Write a minimal HTML document including:
      printWindow.document.write(page_html);

      // 4) Finalize the document
      printWindow.document.close();
      printWindow.focus();

      // 5) Trigger the print dialog
      printWindow.print();

      // 6) (Optional) Close the new window after printing
      setTimeout(() => {
         printWindow.close();
      }, 1000);
   },

   async download_element_as_image({
      element,
      element_id,
      format = 'a4',
      file_name = 'save',
   }: {
      element?: HTMLElement;
      element_id?: string;
      format?: 'a5' | 'a4' | 'a3';
      file_name?: string;
   }) {
      try {
         const img_data = await prints.convert_element_to_image_base64({ element, element_id, format })
         if (!img_data) throw 'No image data found'

         const is_device_native = cap_utils.device.is_device_native();
         if (is_device_native) await cap_utils.filesystem.save_file(file_name, img_data, 'externalStorage');
         else {
            const link = document.createElement('a');
            link.href = img_data;
            link.download = file_name;
            link.click();
            link.remove();
         }
         return true
      }
      catch (error) {
         console.error({ error });
         return false
      }
   },

   async convert_element_to_image_base64({
      element,
      element_id,
      format = 'a4',
   }: {
      element?: HTMLElement;
      element_id?: string;
      format?: 'a5' | 'a4' | 'a3';
   }) {
      try {
         const el = element ? element : element_id ? document.getElementById(element_id) : null;
         if (!el) throw 'No element found'

         let html2canvas = (await import('html2canvas-pro')).default;
         const [imgWidth, pageHeight] = prints.paper_sizes_px[format];

         const canvas = await html2canvas(el, {
            scale: 2,
            windowWidth: imgWidth,
            windowHeight: pageHeight
         });

         // Get the image data from the canvas
         const image_data = canvas.toDataURL('image/png');

         if (!image_data) throw 'No image data found'

         return image_data

      } catch (error) {
         console.error({ error });
         return null
      }
   },
};

const states = {
   disposable_state<T>(initial_value: T, cleanup_fn?: () => void) {
      let value = $state(initial_value);

      // We use a simple let variable, NOT $state, to hold the function.
      // This avoids Proxy issues when trying to call it.
      let current_cleanup: (() => void) | null = cleanup_fn ?? null;

      // This effect runs once on mount and registers the cleanup handler
      $effect(() => {
         return () => {
            // This runs when the component is destroyed
            if (current_cleanup) current_cleanup();
            current_cleanup = null
            value = initial_value
         };
      });

      return {
         // Getter/Setter for the value so it works like a normal object property
         get value() { return value },
         set value(v) { value = v },

         /**
          * Register a new cleanup function (e.g., unsubscribe).
          * IMPORTANT: Automatically runs the *previous* cleanup function if one exists,
          * preventing memory leaks if you subscribe multiple times.
          */
         set_cleanup(fn: () => void) {
            if (current_cleanup) current_cleanup();
            current_cleanup = fn;
         },

         /**
          * Manually run the cleanup (optional, mostly for internal logic)
          */
         dispose() {
            if (current_cleanup) {
               current_cleanup();
               current_cleanup = null;
            }
         }
      };
   },
   create<T>(value: T) {
      const state_value = $state({ value });
      return {
         get value() {
            return state_value.value;
         },
         set value(new_state: T) {
            state_value.value = new_state;
         }
      };
   },
   create_persisted<T>(options: { initial_value: T, key: string }) {
      const { key, initial_value } = options
      const state_value = $state({ value: initial_value });

      if (is_browser) {
         const stored_value = localStorage.getItem(key)
         if (stored_value) state_value.value = JSON.parse(stored_value) as T
         else localStorage.setItem(key, JSON.stringify(initial_value))
      }

      return {
         get value() {
            return state_value.value;
         },
         set value(new_state: T) {
            state_value.value = new_state;
            if (is_browser) localStorage.setItem(key, JSON.stringify(state_value.value))
         },
         persist_current_state() {
            if (is_browser) localStorage.setItem(key, JSON.stringify(state_value.value))
         }
      };
   },
   create_persisted_string<T extends string>(options: { initial_value: T, key: string }) {
      const { key, initial_value } = options
      const state_value = $state({ value: initial_value });

      if (is_browser) {
         const stored_value = localStorage.getItem(key) as T | null
         if (stored_value) state_value.value = stored_value
      }

      return {
         get value() {
            return state_value.value;
         },
         set value(new_state: T) {
            state_value.value = new_state;
            if (is_browser) localStorage.setItem(key, state_value.value)
         }
      };
   },

   create_persisted_string_async<T extends string>(options: { initial_value: T, key: string, init_fn?: (value: T | null) => Promise<T | null | undefined> }) {
      const { key, initial_value, init_fn } = options
      const state_value = $state({ value: initial_value });

      if (is_browser) {
         const stored_value = localStorage.getItem(key) as T | null
         if (stored_value) state_value.value = stored_value
         init_fn?.(stored_value).then(value => { if (value) state_value.value = value })
      }

      return {
         get value() {
            return state_value.value;
         },
         set value(new_state: T) {
            state_value.value = new_state;
            if (is_browser) localStorage.setItem(key, state_value.value)
         }
      };
   },

   create_edit_object_fn<T>(initial_value_fn: () => T) {
      const initial_value = $state.snapshot(initial_value_fn()) as T
      const state_value = $state({ value: $state.snapshot(initial_value_fn()) as T });

      return {
         get value() {
            return state_value.value
         },
         get is_modified() {
            return !array_fns.are_objects_equal(state_value.value, initial_value)
         },
         set value(new_state: T) {
            state_value.value = new_state;
         },
         reset() {
            state_value.value = $state.snapshot(initial_value_fn()) as T
         }
      };
   },

   create_add_object<T>(options: { initial_value: T, validate_fn?: (value: T) => boolean }) {
      const { initial_value, validate_fn } = options

      const initial_value_snapshot = $state.snapshot(initial_value) as T
      const state_value = $state({ value: initial_value as T });

      return {
         get value() {
            return state_value.value
         },
         get is_valid() {
            if (validate_fn) return validate_fn(state_value.value)
            else return true
         },
         set value(new_state: T) {
            state_value.value = new_state;
         },
         reset() {
            state_value.value = $state.snapshot(initial_value_snapshot) as T
         }
      };
   },

};

const functions_fns = {
   running_promises: new Map<() => Promise<any>, Promise<any>>(),

   async run_async_only_one_same_time<T>(func: () => Promise<T>): Promise<T | undefined> {
      // If already running, return the existing promise
      if (functions_fns.running_promises.has(func)) {
         return functions_fns.running_promises.get(func) as Promise<T>;
      }

      // Create and store the new promise
      const promise = func()
         .catch(error => {
            console.error({ error });
            return undefined;
         })
         .finally(() => {
            functions_fns.running_promises.delete(func); // Clean up
         });

      functions_fns.running_promises.set(func, promise);
      return promise;
   }
};

const addresses = {

   /**
    * Generates a Google Maps URL for navigating multiple waypoints.
    * Google Maps limits waypoints, but it works exceptionally well for route segments.
    */
   generate_multi_stop_navigation(
      steps: shipments_routes_steps[],
      start_index: number
   ): string {
      if (!steps || steps.length === 0 || start_index >= steps.length) return '';

      const remaining_steps = steps.slice(start_index).filter(s => s.Address?.location?.coordinates);
      if (remaining_steps.length === 0) return '';

      // Destination is the final step
      const destination = remaining_steps[remaining_steps.length - 1].Address.location.coordinates;
      const dest_lat = destination[1];
      const dest_lng = destination[0];

      // Waypoints are everything in between the current step and the destination
      const waypoints = remaining_steps.slice(0, -1).map(step => {
         const coords = step.Address.location.coordinates;
         return `${coords[1]},${coords[0]}`; // MapLibre is [lng, lat], Google expects [lat, lng]
      }).join('|');

      const base_url = `https://www.google.com/maps/dir/?api=1&destination=${dest_lat},${dest_lng}`;
      return waypoints.length > 0 ? `${base_url}&waypoints=${waypoints}` : base_url;
   },
   create_address_object(location: { longitude: number; latitude: number }) {
      return {
         type: 'Point',
         crs: {
            type: 'name',
            properties: {
               name: 'urn:ogc:def:crs:EPSG::4326'
            }
         },
         coordinates: [location.longitude, location.latitude] as [number, number]
      };
   },
   decode_address_string(hex: string): Scalars['geometry'] | null {
      try {
         function hexToBytes(hex: string): Uint8Array {
            const bytes = new Uint8Array(hex.length / 2);
            for (let i = 0; i < hex.length; i += 2) {
               bytes[i / 2] = parseInt(hex.substr(i, 2), 16);
            }
            return bytes;
         }

         const bytes = hexToBytes(hex);
         const view = new DataView(bytes.buffer);
         let offset = 0;

         // Read byte order (0 = big-endian, 1 = little-endian)
         const byteOrder = view.getUint8(offset);
         offset += 1;
         const littleEndian = byteOrder === 1;

         // Read type code and check for SRID
         const typeCode = view.getUint32(offset, littleEndian);
         offset += 4;
         const hasSrid = (typeCode & 0x20000000) !== 0;
         const geometryType = typeCode & 0x1fffffff;

         // Read SRID if present
         let srid = 0;
         if (hasSrid) {
            srid = view.getUint32(offset, littleEndian);
            offset += 4;
         }

         // Handle geometry type (only Point supported in this implementation)
         let type: string;
         switch (geometryType) {
            case 1:
               type = 'Point';
               break;
            default:
               throw new Error(`Unsupported geometry type: ${geometryType}`);
         }

         // Read coordinates (assuming 2D Point)
         const x = view.getFloat64(offset, littleEndian);
         offset += 8;
         const y = view.getFloat64(offset, littleEndian);

         // Build result
         const result = {
            type,
            coordinates: [x, y]
         } as Scalars['geometry'];

         if (hasSrid) {
            result.crs = {
               type: 'name',
               properties: {
                  name: `EPSG:${srid}`
               }
            };
         }

         return result;
      } catch (error) {
         console.error('############ error at decode_wkb: ', { error });
         return null;
      }
   },

   async navigate_to_coordinates(coordinates: [number, number], address_name = Loc('destination,the')) {
      try {

         if (!coordinates) return false;
         const [{ cap_utils }, { AppLauncher }] = await Promise.all([import('./cap_utils'), import('@capacitor/app-launcher')]);

         const device_os = await cap_utils.device.get_device_os();
         const is_device_native = cap_utils.device.is_device_native();

         const label = address_name ?? ''
         const lat = coordinates[1];
         const lng = coordinates[0];
         const encodedAddress = encodeURIComponent(label);

         // Construct the appropriate URL based on the operating system.
         const url = device_os === 'ios'
            // Apple Maps URL scheme to show a pin with a label.
            ? `maps://?q=${encodedAddress}&ll=${lat},${lng}`
            : device_os === 'android'
               // Android's geo intent to show a labeled pin on the map.
               ? `geo:${lat},${lng}?q=${lat},${lng}(${encodedAddress})`
               // Fallback to Google Maps for web browsers.
               : `https://www.google.com/maps?q=${encodedAddress}@${lat},${lng}`;

         // Use window.location.href for native apps to open the map scheme directly,
         // and window.open for the web to open a new tab.
         if (is_device_native) await AppLauncher.openUrl({ url: url }).catch((e) => window.location.href = url)
         else window.open(url, '_blank');
         return true
      } catch (error) {
         console.error('error at navigate_to_coordinates: ', { error });
         return false
      }

   },
   async navigate_to_coordinates_multiple(origin: string, destination?: string, waypoints: string[] = []) {
      let url = '';

      if (!destination) {
         // Single point routing
         url = `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${origin}&travelmode=driving`;
      } else {
         // Multi-point routing
         const waypoints_param = waypoints.length > 0 ? `&waypoints=${waypoints.join('|')}` : '';
         url = `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}${waypoints_param}&travelmode=driving`;
      }

      try {
         const [{ cap_utils }, { AppLauncher }] = await Promise.all([
            import('./cap_utils'),
            import('@capacitor/app-launcher')
         ]);

         const is_device_native = cap_utils.device.is_device_native();

         if (is_device_native) {
            await AppLauncher.openUrl({ url }).catch(() => {
               window.location.href = url;
            });
         } else {
            window.open(url, '_blank');
         }
      } catch (error) {
         console.error('error at launch_google_maps_route: ', { error });
         window.open(url, '_blank');
      }
   },

   convert_maplibre_env_to_coordinates(event: any) {
      return [event.lngLat.lng, event.lngLat.lat] as [number, number];
   },
   extract_coordinates,

   envs: {
      SERVICE_AREA_GEOJSON: {
         type: 'FeatureCollection',
         features: [
            {
               type: 'Feature',
               properties: {},
               geometry: {
                  coordinates: [
                     [
                        [12.912982655499917, 32.812771989221474],
                        [12.91179317968897, 32.74033451562282],
                        [13.028903291795189, 32.75410457102004],
                        [13.070245518642594, 32.699364189904415],
                        [13.175247321991861, 32.667184134724906],
                        [13.239892452052743, 32.68739071064384],
                        [13.257310291342009, 32.72455074523559],
                        [13.42901625809219, 32.75203029448052],
                        [13.514270386686405, 32.82480772267408],
                        [13.405042046151493, 32.87514102792592],
                        [13.387582689837501, 32.89739731398889],
                        [13.347830384558222, 32.894865651196596],
                        [13.3255465128033, 32.896132143816445],
                        [13.289410186097683, 32.909026135316324],
                        [13.235001788389013, 32.909459788204074],
                        [13.191178380747061, 32.895825793105786],
                        [13.176969289664669, 32.901967553021336],
                        [13.168229052167646, 32.896531856380136],
                        [13.152621979140633, 32.88666008759536],
                        [13.11688088831184, 32.87852595376626],
                        [13.091548338373656, 32.86610934897112],
                        [13.02243528385901, 32.84495305534733],
                        [12.912982655499917, 32.812771989221474]
                     ]
                  ],
                  type: 'Polygon'
               }
            }
         ]
      }
   },
   is_point_in_polygon(lng: number, lat: number, service_area?: typeof this.envs.SERVICE_AREA_GEOJSON) {
      const active_service_area = service_area ?? this.envs.SERVICE_AREA_GEOJSON
      const polygon = active_service_area.features[0].geometry.coordinates[0];

      let inside = false;
      for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
         const xi = polygon[i][0],
            yi = polygon[i][1];
         const xj = polygon[j][0],
            yj = polygon[j][1];

         const intersect = yi > lat !== yj > lat && lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi;
         if (intersect) inside = !inside;
      }
      return inside;
   },

   async parse_location_share_url(url: string) {
      try {
         if (!url) return;

         let latitude = 0,
            longitude = 0;

         const coordinates_response = await this.extract_coordinates(url);

         if (!coordinates_response) throw 'extract_failed';

         if (coordinates_response === 'call_server') {
            const res = await fetch(
               `${import.meta.env.VITE_SERVICES_SITE_URL}/public/marketplace/extract_coordinates_from_url`,
               {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ input: { url } })
               }
            );

            const result = await res.json();
            const extracted_data = (result as any)?.data;

            if (!extracted_data || extracted_data.length !== 2) throw 'extract_failed';

            const [lng, lat] = extracted_data as [number, number];
            if (isFinite(lat) && isFinite(lng)) {
               latitude = lat;
               longitude = lng;
            }
         } else if (coordinates_response?.latitude && coordinates_response?.longitude) {
            latitude = coordinates_response.latitude;
            longitude = coordinates_response.longitude;
         }

         if (isNaN(latitude) || isNaN(longitude)) throw 'extract_failed';

         return { latitude, longitude }
      } catch (error) {
         return false
      }
   }

}


function create_safety_lock(options: { onclick?: HTMLButtonAttributes['onclick'], onsuccess: (e?: any) => any, safety_clicks_fn: () => number | boolean | undefined }) {
   // Internal Safety Logic
   const { onclick, onsuccess, safety_clicks_fn } = options
   let unlock_progress = $state(0);
   let reset_timer: ReturnType<typeof setTimeout> | null = null;

   function get_safety_clicks() {
      const safety_clicks = safety_clicks_fn()
      return safety_clicks === true ? 4 : typeof safety_clicks === 'number' ? safety_clicks : 0
   }

   function handle_interaction(e: any) {
      // 1. If Safety Lock is active
      if (safety_clicks_fn() && unlock_progress < get_safety_clicks()) {
         e.preventDefault();
         e.stopPropagation();

         // Increment progress
         unlock_progress += 1;

         // Reset progress if user stops clicking for 2 seconds
         if (reset_timer) clearTimeout(reset_timer);
         reset_timer = setTimeout(() => {
            unlock_progress = 0;
         }, 2000);

         return;
      }

      // 2. Standard Logic (Unlocked)
      if (onsuccess) onsuccess(e);
      else onclick?.(e);
      reset()
   }

   function reset() {
      unlock_progress = 0
      if (reset_timer) clearTimeout(reset_timer);
   }

   return {
      get is_active() { return get_safety_clicks() },
      get unlock_progress() {
         return unlock_progress;
      },
      get clicks_remaining() {
         return safety_clicks_fn() ? get_safety_clicks() - unlock_progress : 0;
      },

      get is_locked() {
         return safety_clicks_fn() && unlock_progress < get_safety_clicks();
      },
      reset,
      onclick: handle_interaction
   };
}


export const utils = {
   addresses,
   encryptions,
   functions_fns,
   create_safety_lock,
   states,
   shipments: shipments_utils,
   forms,
   text_fns,
   object_fns,
   dates,
   array_fns,
   cn,
   numbers,
   files,
   images,
   prints,
   shares: share_handlers,
   jwt: jwt_utils,
   is_browser,
   lazy_load_script,
   actions
};
